import { ErrorCodes } from '@/redux/api/base';
import { takeEvery, put, take, race, fork, cancel, takeLeading } from 'redux-saga/effects';
import {
  clearMfaRequired,
  setMfaRequired,
  enableMfaUserLock,
  otpCodeSubmit,
  setMfaSetupRequired,
} from '@/redux/slices/auth';
import { api } from '@/redux/api/base';
import { toast } from 'react-toastify';

function* handleLockedListener() {
  yield takeEvery(
    (action: any) => action.payload?.status === ErrorCodes.LOCKED,
    function* () {
      yield put(enableMfaUserLock());
    },
  );
}

function* handleMfaRequired(action: any) {
  try {
    const originalAction = action?.meta?.arg;

    if (action?.payload?.status === ErrorCodes.LOCKED) {
      yield put(enableMfaUserLock());
      return;
    }

    if (action?.payload?.status === ErrorCodes.MFA_REQUIRED) {
      yield put(setMfaRequired());
    }

    if (action?.payload?.status === ErrorCodes.MFA_SETUP_REQUIRED) {
      yield put(
        setMfaSetupRequired({
          qrCode: action?.payload?.data?.meta?.qrCode,
          totpSecret: action?.payload?.data?.meta?.totpSecret,
          totpUri: action?.payload?.data?.meta?.totpUri,
        }),
      );
    }

    while (true) {
      const mfaCodeAction = yield take(otpCodeSubmit.type);

      if (mfaCodeAction.payload) {
        const mfaRequest = yield put(
          api.endpoints[originalAction?.endpointName].initiate({
            ...originalAction?.originalArgs,
            mfaCode: mfaCodeAction.payload,
          }),
        );

        const { success, mfaFailure, apiFailure, cancelMfa } = yield race({
          success: take(
            (action) =>
              action?.meta?.arg?.endpointName === originalAction?.endpointName && action?.type?.endsWith('fulfilled'),
          ),
          mfaFailure: take((action) => action?.payload?.status === ErrorCodes.INVALID_MFA_CODE),
          apiFailure: take(
            (action) =>
              action?.meta?.arg?.endpointName === originalAction?.endpointName && action?.type?.endsWith('rejected'),
          ),
          cancelMfa: take(clearMfaRequired.type),
        });

        if (apiFailure) {
          toast.error(apiFailure?.payload?.data?.message ?? 'An error occurred');
          yield put(clearMfaRequired());
          return;
        }

        if (cancelMfa) {
          yield cancel();
          return;
        }

        if (success) {
          if (!originalAction?.originalArgs?.meta?.toast?.success) {
            toast.success('MFA code verified');
          }

          yield put(clearMfaRequired());
          return;
        }

        if (mfaFailure) {
          toast.error('Invalid MFA code');
        }
      }
    }
  } catch (error) {
    console.error('mfa required saga error', error);
  }
}

export function* mfaRequiredSaga() {
  yield takeLeading((action: any) => {
    return (
      action?.payload?.status === ErrorCodes.MFA_REQUIRED ||
      action?.payload?.status === ErrorCodes.MFA_SETUP_REQUIRED ||
      action?.payload?.status === ErrorCodes.LOCKED
    );
  }, handleMfaRequired);

  yield fork(handleLockedListener);
}
